import { useEffect, useState } from 'react';

import { Link, useLocation, useParams } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../backend/src/organisation/organisation-permission';
import { useLogout } from '../../api/mutations/useLogout';
import { useGetSubset } from '../../api/queries/useGetSubset';
import { useUserContext } from '../../Contexts/User';
import { useOrgPermissions } from '../../hooks/orgPermissions';
import { useHasPermission } from '../../hooks/useHasPermission';
import { getFrontendURLRegion } from '../../utils/checkFrontendURL';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Icon } from '@/new/components/icon';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/new/components/shadcn/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SidebarAnnouncement } from '@/new/components/sidebar-announcement';
import { cn } from '@/new/lib/utils';

interface SidebarLink {
  text: string;
  iconClass: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  requiredPermissions?: Permission[];
  pathRegex?: RegExp;
  disabledMessage?: string;
  requiredOrgPermission?: OrgPermission;
}

interface SidebarLinkGroup {
  heading?: string;
  headingIconClass?: string;
  headingClassName?: string;
  items: SidebarLink[];
}

export function AppSidebar() {
  const params = useParams();
  const location = useLocation();
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { isEnabled: isOrgFeatureEnabled } = useOrgPermissions();

  const logout = useLogout();
  const [sidebarItems, setSidebarItems] = useState<SidebarLinkGroup[]>();

  const { data: matter } = useGetMatterV2(params.matterId);
  const { data: subset } = useGetSubset(params.matterId, params.subsetId);

  const rootItems: SidebarLinkGroup[] = [
    { items: [{ iconClass: 'lucide--briefcase', text: 'Matters', href: '/matters' }] },
    {
      heading: 'Organisation',
      items: [
        {
          iconClass: 'lucide--chart-no-axes-combined',
          text: 'Usage',
          // Legacy usage stays reachable from the new dashboard, so keep the item active on both routes.
          href: '/usage',
          pathRegex: /^\/(legacy-)?usage(\/|$)/,
          requiredPermissions: [Permission.UsageDashboardView, Permission.PlatformViewUsage],
        },
        {
          iconClass: 'lucide--users-round',
          text: 'Manage Users',
          href: '/manage-users',
          requiredPermissions: [Permission.UserManage],
        },
        {
          iconClass: 'lucide--settings',
          text: 'Settings',
          href: '/organisation-settings',
          pathRegex: /^\/organisation-settings(\/|$)/,
          requiredPermissions: [
            Permission.OrganisationManage,
            Permission.IntegrationsManage,
            Permission.TokenLimitsManage,
            Permission.UserManage,
          ],
        },
      ],
    },
    {
      heading: 'Learn',
      items: [
        {
          iconClass: 'lucide--book-open',
          text: 'Help Centre',
          href: '/help-centre',
          pathRegex: /^\/help-centre(\/|$)/,
        },
        { iconClass: 'lucide--notebook', text: 'Release Notes', href: '/release-notes' },
      ],
    },
    {
      heading: 'Platform Admin',
      items: [
        {
          iconClass: 'lucide--shield-user',
          text: 'Organisations',
          href: '/admin/organisations',
          pathRegex: /^\/admin\/organisations(?:\/|$)/,
          requiredPermissions: [
            Permission.PlatformManageOrganisations,
            Permission.PlatformDeleteOrganisations,
            Permission.PlatformManageBilling,
            Permission.PlatformManageUsers,
            Permission.PlatformViewUsage,
            Permission.PlatformManageRuns,
            Permission.PlatformViewFeedback,
          ],
        },
        {
          iconClass: 'lucide--activity',
          text: 'Recent Runs',
          href: '/admin/runs',
          requiredPermissions: [Permission.PlatformManageRuns],
        },
        {
          iconClass: 'lucide--banknote',
          text: 'LLM Pricing',
          href: '/admin/llm-pricing',
          requiredPermissions: [Permission.PlatformManageLlmPricing],
        },
      ],
    },
  ];

  const allDocumentsSubsetId = matter?.allDocumentsCaseId ?? null;
  const isAllDocumentsNavigationLoading = params.matterId !== undefined && !allDocumentsSubsetId;
  const getAllDocumentsHref = (path: string) =>
    allDocumentsSubsetId ? `/matters/${params.matterId}/subsets/${allDocumentsSubsetId}${path}` : undefined;
  const allDocumentsLoadingProps = isAllDocumentsNavigationLoading
    ? {
        disabled: true,
        disabledMessage: 'Navigation Loading',
      }
    : {};

  const kimPath = 'new-kim';

  const matterItems: SidebarLinkGroup[] = [
    {
      items: [
        {
          iconClass: 'lucide--info',
          text: 'Overview',
          href: `/matters/${params.matterId}`,
        },
        {
          iconClass: 'lucide--bot',
          text: 'Kim',
          href: getAllDocumentsHref(`/${kimPath}`),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/(new-)?kim/,
          ...allDocumentsLoadingProps,
        },
        {
          iconClass: 'lucide--file-text',
          text: 'Documents',
          href: getAllDocumentsHref('/docs'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/docs/,
          ...allDocumentsLoadingProps,
        },
        {
          iconClass: 'lucide--group',
          text: 'Subsets',
          href: `/matters/${params.matterId}/subsets`,
          pathRegex: /\/matters\/[^/]+\/subsets(\/(new)?)?$/,
        },
      ],
    },
    // Subset-level items linked to All Documents subset
    {
      heading: 'Work Product',
      items: [
        {
          iconClass: 'lucide--git-fork',
          text: 'Chronologies',
          href: getAllDocumentsHref('/chronologies'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies(?!\/doc)/,
          ...allDocumentsLoadingProps,
        },
        {
          iconClass: 'lucide--folder-git-2',
          text: 'Doc Chronologies',
          href: getAllDocumentsHref('/chronologies/doc'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies\/doc/,
          ...allDocumentsLoadingProps,
        },
        ...(getFrontendURLRegion() === 'au'
          ? []
          : [
              {
                iconClass: 'lucide--radio',
                text: 'Realtime',
                href: getAllDocumentsHref('/realtime'),
                pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/realtime/,
                requiredPermissions: [Permission.RealtimeUse],
                ...allDocumentsLoadingProps,
              },
            ]),
      ],
    },
    {
      heading: 'Data',
      items: [
        {
          iconClass: 'lucide--search',
          text: 'Facts',
          href: getAllDocumentsHref('/facts'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/facts/,
          ...allDocumentsLoadingProps,
        },
        {
          iconClass: 'lucide--shield-alert',
          text: 'Inconsistencies',
          href: getAllDocumentsHref('/inconsistencies'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/inconsistencies/,
          ...allDocumentsLoadingProps,
        },
        {
          iconClass: 'lucide--users-round',
          text: 'Entities',
          href: getAllDocumentsHref('/entities'),
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/entities/,
          ...allDocumentsLoadingProps,
        },
      ],
    },
    {
      heading: 'Configure',
      items: [
        {
          iconClass: 'lucide--settings',
          text: 'Settings',
          href: `/matters/${params.matterId}/settings`,
          pathRegex: /\/matters\/[^/]+\/settings/,
          requiredPermissions: [
            Permission.MatterEdit,
            Permission.DocumentDelete,
            Permission.DocumentDownload,
            Permission.RelativityImport,
            Permission.MatterViewUsers,
            Permission.MatterCreate,
            Permission.AuditView,
          ],
        },
      ],
    },
  ];

  const subsetItems: SidebarLinkGroup[] = [
    {
      heading: subset?.name ?? '',
      headingIconClass: 'lucide--group',
      items: [
        {
          iconClass: 'lucide--info',
          text: 'Overview',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}`,
        },
        {
          iconClass: 'lucide--bot',
          text: 'Kim',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/${kimPath}`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/(new-)?kim/,
        },
        {
          iconClass: 'lucide--file-text',
          text: 'Documents',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/docs`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/docs/,
        },
      ],
    },
    {
      heading: 'Work Product',
      items: [
        {
          iconClass: 'lucide--git-fork',
          text: 'Chronologies',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/chronologies`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies(?!\/doc)/,
        },
        {
          iconClass: 'lucide--folder-git-2',
          text: 'Doc Chronologies',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/chronologies/doc`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/chronologies\/doc/,
        },
        ...(getFrontendURLRegion() === 'au'
          ? []
          : [
              {
                iconClass: 'lucide--radio',
                text: 'Realtime',
                href: `/matters/${params.matterId}/subsets/${params.subsetId}/realtime`,
                pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/realtime/,
                requiredPermissions: [Permission.RealtimeUse],
              },
            ]),
      ],
    },
    {
      heading: 'Data',
      items: [
        {
          iconClass: 'lucide--search',
          text: 'Facts',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/facts`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/facts/,
        },
        {
          iconClass: 'lucide--shield-alert',
          text: 'Inconsistencies',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/inconsistencies`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/inconsistencies/,
        },
        {
          iconClass: 'lucide--users-round',
          text: 'Entities',
          href: `/matters/${params.matterId}/subsets/${params.subsetId}/entities`,
          pathRegex: /\/matters\/[^/]+\/subsets\/[^/]+\/entities/,
        },
      ],
    },
  ];

  // Set sidebar items depending on route
  useEffect(() => {
    // If viewing All Documents subset, show Matter context items (not subset items)
    if (params.subsetId && allDocumentsSubsetId !== params.subsetId) return setSidebarItems(subsetItems);
    // Don't switch to matter context if we're in the creator flow
    if (params.matterId && !location.pathname.includes('/matters/new/')) return setSidebarItems(matterItems);
    setSidebarItems(rootItems);
  }, [location, allDocumentsSubsetId, subset?.name]);

  const { state } = useSidebar();
  const isInSubsetContext = params.subsetId !== undefined && allDocumentsSubsetId !== params.subsetId;

  return (
    <Sidebar variant="inset" collapsible="icon" className="static w-56" data-tour="sidebar">
      <SidebarContent className="p-2.5 py-1.5 pr-0">
        <SidebarHeader onClick={(e) => e.stopPropagation()} className="-ml-0.5">
          <Link to="/" className="relative h-5 w-24">
            <img
              src="/logo-full.svg"
              alt="wexler.ai"
              className={`${state === 'collapsed' ? 'opacity-0' : 'opacity-100'} absolute transition-opacity duration-100`}
            />
            <img src="/logo-w.svg" alt="wexler.ai" />
          </Link>

          <DropdownMenu size="sm">
            <DropdownMenuTrigger
              className={`${state === 'collapsed' ? 'h-0 opacity-0' : 'h-10 opacity-100'} flex w-full items-center justify-between gap-1 pr-1 transition-[height,opacity] duration-100`}
            >
              <div className="space-y-0.5 overflow-hidden text-left text-xs">
                <p className="line-clamp-1 text-ellipsis">{user?.email}</p>
                <p className="line-clamp-1 text-ellipsis text-gray-500">{user?.organisation?.name}</p>
              </div>
              <Icon className="lucide--chevron-down text-gray-500" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              onCloseAutoFocus={(e) => e.preventDefault()}
              align="end"
              className="min-w-32"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => logout.mutate()}>
                  <Icon className="lucide--log-out" /> Log Out
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Icon className="lucide--settings-2" /> User Settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarHeader>

        {sidebarItems &&
          sidebarItems.map((group, i) => {
            // Compute visibility and disabled state per item
            const visibleItems = group.items
              .map((item) => {
                if (item.requiredOrgPermission && !isOrgFeatureEnabled(item.requiredOrgPermission)) return null;
                if (item.disabled) return { ...item, permissionDisabled: true };
                if (item.requiredPermissions) {
                  const anyAllowed = item.requiredPermissions.some((p) => hasPermission(p));
                  if (anyAllowed) return { ...item, permissionDisabled: false };
                  return null; // hidden when lacking all required permissions
                }
                return { ...item, permissionDisabled: false };
              })
              .filter(Boolean) as (SidebarLink & { permissionDisabled: boolean })[];

            if (visibleItems.length === 0) return null;

            return (
              <SidebarGroup key={i}>
                {group.heading !== undefined && (
                  <SidebarGroupLabel className={group.headingClassName}>
                    {group.headingIconClass && <span className={cn('iconify shrink-0', group.headingIconClass)}></span>}
                    <span className="truncate">{group.heading}</span>
                  </SidebarGroupLabel>
                )}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleItems.map((item) => {
                      const selected = item.pathRegex
                        ? item.pathRegex.test(location.pathname)
                        : item.href === location.pathname;
                      const disabledMsg = item.disabledMessage ?? 'You do not have permission to access this';
                      const itemKey = `${group.heading ?? 'main'}-${item.text}`;

                      return (
                        <SidebarMenuItem
                          className={cn(
                            'w-full transition-opacity duration-150 ease-in-out',
                            item.permissionDisabled ? 'opacity-50' : 'opacity-100',
                          )}
                          key={itemKey}
                        >
                          {item.permissionDisabled ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="pointer-events-auto cursor-not-allowed">
                                  <SidebarMenuButton className="group pointer-events-none" tooltip={item.text}>
                                    <span className={cn('iconify', item.iconClass)}></span>
                                    <span className="line-clamp-1 overflow-ellipsis whitespace-nowrap">
                                      {item.text}
                                    </span>
                                  </SidebarMenuButton>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent align="start" alignOffset={-8} sideOffset={0}>
                                {disabledMsg}
                              </TooltipContent>
                            </Tooltip>
                          ) : item.href ? (
                            <SidebarMenuButton asChild className="group" tooltip={item.text} isActive={selected}>
                              <Link to={item.href}>
                                <span
                                  className={cn(
                                    'iconify',
                                    item.iconClass,
                                    selected ? (isInSubsetContext ? '!text-sky-500' : '!text-teal-500') : undefined,
                                  )}
                                ></span>
                                <div className="flex items-center gap-3.25">
                                  <span className="line-clamp-1 overflow-ellipsis whitespace-nowrap">{item.text}</span>
                                  <span
                                    className={cn(
                                      'rounded-full p-0.5',
                                      selected ? 'opacity-100' : 'opacity-0',
                                      isInSubsetContext ? 'bg-sky-400' : 'bg-teal-400',
                                    )}
                                  ></span>
                                </div>
                              </Link>
                            </SidebarMenuButton>
                          ) : (
                            <SidebarMenuButton onClick={item.onClick} tooltip={item.text}>
                              <span className={cn('iconify', item.iconClass)}></span>
                              <span>{item.text}</span>
                            </SidebarMenuButton>
                          )}
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
      </SidebarContent>

      <SidebarFooter className="-m-0.5 p-0">
        <SidebarAnnouncement />
      </SidebarFooter>
    </Sidebar>
  );
}
